import React, {useEffect, useMemo, useState} from "react";
import {useDispatch} from "react-redux";
import {css, cx} from "@panda/css";

import {Button, IButtonProps} from "@pg-design/button-module";
import {PhoneIcon} from "@pg-design/icons-module";
import {useBoundingClientRect} from "@pg-mono/hooks";
import {isEmpty} from "@pg-mono/nodash";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {Currency} from "@pg-rp/common-types";
import {OfferStatusStatus} from "@pg-rp/common-types/src/types/OfferStatusStatus";

import {ApplicationModalButtonWithHiddenPrices} from "../../application/components/ApplicationModalButtonWithHiddenPrices";
import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {getApplicationLeadButtonText} from "../../application/utils/lead/get_application_lead_button_text";
import {useShouldShowPriceByOfferConfigAndUser} from "../../hooks/use_should_show_price_by_offer_config_and_user";
import {useBottomNavBarOffset} from "../../hooks/useBottomNavBarOffset";
import {IOfferSalesOffice} from "../../offer/types/IOfferSalesOffice";
import {ISalesOffice} from "../../offer/types/ISalesOffice";
import {OfferModalActiveTab} from "../../offer/types/OfferModalActiveTab";
import {
    offerViewPhoneNumberClick,
    propertyViewPhoneNumberClick,
    vendorViewPhoneNumberClick
} from "../../tracking/algolytics_hits/click_actions/phone_number_click";
import {ITrackedOffer} from "../../tracking/algolytics_hits/tracked_utils/tracked_offer";
import {ITrackedProperty} from "../../tracking/algolytics_hits/tracked_utils/tracked_property";
import {ITrackedVendor} from "../../tracking/algolytics_hits/tracked_utils/tracked_vendor";
import {trackAmplitudeButtonClick} from "../../tracking/amplitude/track_amplitude_button_click";
import {IGTMEventLeadForm} from "../../tracking/google_tag_manager/gtm_event_form_open";
import {gtmEventPreFormOpen} from "../../tracking/google_tag_manager/gtm_event_pre_form_open";
import {frontendGtmPhoneNumberCall} from "../../tracking/google_tag_manager/hit_actions/gtm_phone_number_call";
import {AmplitudeAuthPlacement} from "../../tracking/types/AmplitudeAuthPlacement";
import {hotlineTelNumber} from "../../types/HotlineTelNumber";
import {setBottomFixedElementHeight} from "../../ui/actions/ui_actions";
import {CallModal} from "../call_modal/CallModal";

interface IProps {
    salesOffices?: ISalesOffice[];
    vendor: {
        id: number;
        logo: {
            v_log_120x90: string;
        } | null;
        logo_url: string | null;
        name: string;
        offices?: {
            mobile: string;
            phone: string;
        }[];
    };
    trackVendor: ITrackedVendor;
    trackOffer?: ITrackedOffer;
    trackProperty?: ITrackedProperty;
    backgroundColor?: string;
    source: ApplicationSource;
    isAutoresponderEnabled: boolean;
    className?: string;
    offer: {
        id: number;
        name: string;
        configuration: {
            personal_data_processor_name?: string | null;
            personal_data_processor_url?: string | null;
            privacy_policy_url?: string | null;
            is_autoresponder_enabled?: boolean;
            phone_clip: string | null;
        };
        is_phone_clip_enabled: boolean;
        sales_offices?: IOfferSalesOffice[];
        status: {
            status: OfferStatusStatus;
        };
        currency: Currency;
    };
    propertyPrice?: number | null;
    propertyId?: number;
    footer?: React.ReactNode;
    buttonsSize?: IButtonProps["size"];
    modalState?: OfferModalActiveTab | null;
    amplitudeAuthPlacement?: AmplitudeAuthPlacement;
}

export const ContactBar = (props: IProps) => {
    const [phoneModalOpen, setPhoneModalOpen] = useState(false);

    const buttonSize = props.buttonsSize || "small";
    const dispatch = useDispatch();
    const isOfferStatusToAcquire = props?.offer?.status?.status === OfferStatusStatus.STATUS_STATUS_TO_ACQUIRE;

    const foundPhone = useMemo(() => {
        if (isOfferStatusToAcquire) {
            return hotlineTelNumber;
        }
        const foundOffice = props.salesOffices && props.salesOffices.find((o) => !isEmpty(o.phone) || !isEmpty(o.mobile));

        if (props.offer.is_phone_clip_enabled && props.offer.configuration.phone_clip) {
            return props.offer.configuration.phone_clip;
        } else if (foundOffice) {
            return foundOffice && (!isEmpty(foundOffice.phone) ? foundOffice.phone : foundOffice.mobile);
        } else if (props.vendor.offices && props.vendor.offices.length !== 0) {
            const foundOfficeWithPhone = props.vendor.offices.find((o) => !isEmpty(o.phone) || !isEmpty(o.mobile));
            return foundOfficeWithPhone && (!isEmpty(foundOfficeWithPhone.phone) ? foundOfficeWithPhone.phone : foundOfficeWithPhone.mobile);
        }
    }, [props.salesOffices, props.offer.is_phone_clip_enabled, props.offer.configuration.phone_clip, props.vendor.offices]);

    const getSourceCategory = () => {
        switch (props.modalState) {
            case OfferModalActiveTab.MAP:
                return GTMEventApplicationSourceSection.MODAL_MAP;
            case OfferModalActiveTab.PLAN:
                return GTMEventApplicationSourceSection.MODAL_FLOOR_PLAN;
            case OfferModalActiveTab.CONSTRUCTION_GALLERY:
                return GTMEventApplicationSourceSection.MODAL_CONSTRUCTION_GALLERY;
            case OfferModalActiveTab.GALLERY:
                return GTMEventApplicationSourceSection.MODAL_GALLERY;
            default:
                return GTMEventApplicationSourceSection.HEADER_LEAD;
        }
    };

    const trackUser = () => {
        // Algolytics
        if (props.trackProperty && props.trackOffer) {
            propertyViewPhoneNumberClick(props.trackProperty, props.trackOffer, props.trackVendor);
        } else if (props.trackOffer) {
            offerViewPhoneNumberClick(props.trackOffer, props.trackVendor);
        } else {
            vendorViewPhoneNumberClick(props.trackVendor);
        }
        // Analytics (GTM)
        frontendGtmPhoneNumberCall(props.trackVendor.id, props.trackOffer && props.trackOffer.id, props.trackProperty && props.trackProperty.id);
    };
    useBottomNavBarOffset();

    const {elementRef, boundingClientRect} = useBoundingClientRect<HTMLDivElement>(null);

    useEffect(() => {
        if (boundingClientRect) {
            dispatch(setBottomFixedElementHeight(boundingClientRect.height ?? null));
        }
    }, [boundingClientRect]);

    const contactPhoneModalOpen = () => {
        setPhoneModalOpen(true);
        gtmEventPreFormOpen("ZADZWOŃ", GTMEventApplicationSourceSection.HEADER_CALL, props.vendor.name, props.offer.name);

        trackAmplitudeButtonClick({
            buttonName: "offer_call_investor",
            buttonCategory: props.propertyId ? "property_page" : "investment_page",
            itemId: props.offer.id,
            propertyId: props.propertyId,
            buttonLink: foundPhone
        });
    };

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        vendorId: props.vendor.id
    });

    const buttonText = getApplicationLeadButtonText({
        isPriceHidden: !shouldShowPrice,
        isProperty: !!props.propertyId,
        propertyPrice: props.propertyPrice,
        preferShortFormat: !!props.propertyId
    });

    const amplitudePlacement: AmplitudeAuthPlacement = props.amplitudeAuthPlacement ?? (props.modalState ? "rp_contact_bar_gallery" : "rp_contact_bar_main");

    const gtmData: IGTMEventLeadForm = {
        clickText: buttonText,
        source: getSourceCategory(),
        vendorName: props.vendor.name,
        offerName: props.offer.name
    };

    return (
        <>
            <div
                ref={elementRef}
                className={cx(bottomBarWrapperStyle, props.className)}
                style={{backgroundColor: props.backgroundColor ?? "#fff"}}
                data-testid={OFFER_TEST_IDS.OFFER_VIEW.CONTACT_BAR_MOBILE}
            >
                <ApplicationModalButtonWithHiddenPrices
                    source={props.source}
                    sourceSection={ApplicationSourceSection.MODAL}
                    vendorId={props.vendor.id}
                    offerId={props.offer.id}
                    propertyId={props.propertyId || null}
                    shouldShowPrice={shouldShowPrice}
                    gtmData={gtmData}
                    amplitudePlacement={amplitudePlacement}
                    render={({handleClick}) => (
                        <Button
                            variant="filled_primary"
                            type="button"
                            size={buttonSize}
                            onClick={handleClick}
                            className={buttonStyle}
                            dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_LEAD_FORM_MOBILE}
                        >
                            {buttonText}
                        </Button>
                    )}
                />

                {foundPhone && (
                    <Button
                        iconLeft={PhoneIcon}
                        variant="filled_secondary"
                        size={buttonSize}
                        className={cx(buttonStyle, css({borderColor: "white"}))}
                        onClick={contactPhoneModalOpen}
                        dataTestId={OFFER_TEST_IDS.OFFER_VIEW.CALL_BUTTON_MOBILE}
                    >
                        Zadzwoń
                    </Button>
                )}
                {props.footer}
            </div>

            <CallModal
                isOpen={phoneModalOpen}
                closeModal={() => setPhoneModalOpen(false)}
                vendor={props.vendor}
                offer={props.offer}
                propertyPrice={props.propertyPrice}
                propertyId={props.propertyId || undefined}
                phoneNumber={foundPhone}
                trackUser={trackUser}
            />
        </>
    );
};

const buttonStyle = css({
    minWidth: 0,
    width: "100%"
});

const bottomBarWrapperStyle = css({
    py: 1,
    px: 0.5,
    position: "fixed",
    zIndex: 11,
    bottom: 0,
    left: 0,
    width: "100%",
    boxShadow: "0 2px 20px rgba(0, 0, 0, 0.2)",
    display: "flex",
    flexDirection: "row-reverse",
    gap: 0.5,

    md: {
        display: "none"
    }
});
import React, {useEffect, useMemo, useState} from "react";
import {useDispatch} from "react-redux";
import {css, cx} from "@panda/css";

import {Button, IButtonProps} from "@pg-design/button-module";
import {PhoneIcon} from "@pg-design/icons-module";
import {useBoundingClientRect} from "@pg-mono/hooks";
import {isEmpty} from "@pg-mono/nodash";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {Currency} from "@pg-rp/common-types";
import {OfferStatusStatus} from "@pg-rp/common-types/src/types/OfferStatusStatus";

import {ApplicationModalButtonWithHiddenPrices} from "../../application/components/ApplicationModalButtonWithHiddenPrices";
import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {getApplicationLeadButtonText} from "../../application/utils/lead/get_application_lead_button_text";
import {useShouldShowPriceByOfferConfigAndUser} from "../../hooks/use_should_show_price_by_offer_config_and_user";
import {useBottomNavBarOffset} from "../../hooks/useBottomNavBarOffset";
import {IOfferSalesOffice} from "../../offer/types/IOfferSalesOffice";
import {ISalesOffice} from "../../offer/types/ISalesOffice";
import {OfferModalActiveTab} from "../../offer/types/OfferModalActiveTab";
import {
    offerViewPhoneNumberClick,
    propertyViewPhoneNumberClick,
    vendorViewPhoneNumberClick
} from "../../tracking/algolytics_hits/click_actions/phone_number_click";
import {ITrackedOffer} from "../../tracking/algolytics_hits/tracked_utils/tracked_offer";
import {ITrackedProperty} from "../../tracking/algolytics_hits/tracked_utils/tracked_property";
import {ITrackedVendor} from "../../tracking/algolytics_hits/tracked_utils/tracked_vendor";
import {trackAmplitudeButtonClick} from "../../tracking/amplitude/track_amplitude_button_click";
import {IGTMEventLeadForm} from "../../tracking/google_tag_manager/gtm_event_form_open";
import {gtmEventPreFormOpen} from "../../tracking/google_tag_manager/gtm_event_pre_form_open";
import {frontendGtmPhoneNumberCall} from "../../tracking/google_tag_manager/hit_actions/gtm_phone_number_call";
import {AmplitudeAuthPlacement} from "../../tracking/types/AmplitudeAuthPlacement";
import {hotlineTelNumber} from "../../types/HotlineTelNumber";
import {setBottomFixedElementHeight} from "../../ui/actions/ui_actions";
import {CallModal} from "../call_modal/CallModal";

interface IProps {
    salesOffices?: ISalesOffice[];
    vendor: {
        id: number;
        logo: {
            v_log_120x90: string;
        } | null;
        logo_url: string | null;
        name: string;
        offices?: {
            mobile: string;
            phone: string;
        }[];
    };
    trackVendor: ITrackedVendor;
    trackOffer?: ITrackedOffer;
    trackProperty?: ITrackedProperty;
    backgroundColor?: string;
    source: ApplicationSource;
    isAutoresponderEnabled: boolean;
    className?: string;
    offer: {
        id: number;
        name: string;
        configuration: {
            personal_data_processor_name?: string | null;
            personal_data_processor_url?: string | null;
            privacy_policy_url?: string | null;
            is_autoresponder_enabled?: boolean;
            phone_clip: string | null;
        };
        is_phone_clip_enabled: boolean;
        sales_offices?: IOfferSalesOffice[];
        status: {
            status: OfferStatusStatus;
        };
        currency: Currency;
    };
    propertyPrice?: number | null;
    propertyId?: number;
    footer?: React.ReactNode;
    buttonsSize?: IButtonProps["size"];
    modalState?: OfferModalActiveTab | null;
    amplitudeAuthPlacement?: AmplitudeAuthPlacement;
}

export const ContactBar = (props: IProps) => {
    const [phoneModalOpen, setPhoneModalOpen] = useState(false);

    const buttonSize = props.buttonsSize || "small";
    const dispatch = useDispatch();
    const isOfferStatusToAcquire = props?.offer?.status?.status === OfferStatusStatus.STATUS_STATUS_TO_ACQUIRE;

    const foundPhone = useMemo(() => {
        if (isOfferStatusToAcquire) {
            return hotlineTelNumber;
        }
        const foundOffice = props.salesOffices && props.salesOffices.find((o) => !isEmpty(o.phone) || !isEmpty(o.mobile));

        if (props.offer.is_phone_clip_enabled && props.offer.configuration.phone_clip) {
            return props.offer.configuration.phone_clip;
        } else if (foundOffice) {
            return foundOffice && (!isEmpty(foundOffice.phone) ? foundOffice.phone : foundOffice.mobile);
        } else if (props.vendor.offices && props.vendor.offices.length !== 0) {
            const foundOfficeWithPhone = props.vendor.offices.find((o) => !isEmpty(o.phone) || !isEmpty(o.mobile));
            return foundOfficeWithPhone && (!isEmpty(foundOfficeWithPhone.phone) ? foundOfficeWithPhone.phone : foundOfficeWithPhone.mobile);
        }
    }, [props.salesOffices, props.offer.is_phone_clip_enabled, props.offer.configuration.phone_clip, props.vendor.offices]);

    const getSourceCategory = () => {
        switch (props.modalState) {
            case OfferModalActiveTab.MAP:
                return GTMEventApplicationSourceSection.MODAL_MAP;
            case OfferModalActiveTab.PLAN:
                return GTMEventApplicationSourceSection.MODAL_FLOOR_PLAN;
            case OfferModalActiveTab.CONSTRUCTION_GALLERY:
                return GTMEventApplicationSourceSection.MODAL_CONSTRUCTION_GALLERY;
            case OfferModalActiveTab.GALLERY:
                return GTMEventApplicationSourceSection.MODAL_GALLERY;
            default:
                return GTMEventApplicationSourceSection.HEADER_LEAD;
        }
    };

    const trackUser = () => {
        // Algolytics
        if (props.trackProperty && props.trackOffer) {
            propertyViewPhoneNumberClick(props.trackProperty, props.trackOffer, props.trackVendor);
        } else if (props.trackOffer) {
            offerViewPhoneNumberClick(props.trackOffer, props.trackVendor);
        } else {
            vendorViewPhoneNumberClick(props.trackVendor);
        }
        // Analytics (GTM)
        frontendGtmPhoneNumberCall(props.trackVendor.id, props.trackOffer && props.trackOffer.id, props.trackProperty && props.trackProperty.id);
    };
    useBottomNavBarOffset();

    const {elementRef, boundingClientRect} = useBoundingClientRect<HTMLDivElement>(null);

    useEffect(() => {
        if (boundingClientRect) {
            dispatch(setBottomFixedElementHeight(boundingClientRect.height ?? null));
        }
    }, [boundingClientRect]);

    const contactPhoneModalOpen = () => {
        setPhoneModalOpen(true);
        gtmEventPreFormOpen("ZADZWOŃ", GTMEventApplicationSourceSection.HEADER_CALL, props.vendor.name, props.offer.name);

        trackAmplitudeButtonClick({
            buttonName: "offer_call_investor",
            buttonCategory: props.propertyId ? "property_page" : "investment_page",
            itemId: props.offer.id,
            propertyId: props.propertyId,
            buttonLink: foundPhone
        });
    };

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        vendorId: props.vendor.id
    });

    const buttonText = getApplicationLeadButtonText({
        isPriceHidden: !shouldShowPrice,
        isProperty: !!props.propertyId,
        propertyPrice: props.propertyPrice,
        preferShortFormat: !!props.propertyId
    });

    const amplitudePlacement: AmplitudeAuthPlacement = props.amplitudeAuthPlacement ?? (props.modalState ? "rp_contact_bar_gallery" : "rp_contact_bar_main");

    const gtmData: IGTMEventLeadForm = {
        clickText: buttonText,
        source: getSourceCategory(),
        vendorName: props.vendor.name,
        offerName: props.offer.name
    };

    return (
        <>
            <div
                ref={elementRef}
                className={cx(bottomBarWrapperStyle, props.className)}
                style={{backgroundColor: props.backgroundColor ?? "#fff"}}
                data-testid={OFFER_TEST_IDS.OFFER_VIEW.CONTACT_BAR_MOBILE}
            >
                <ApplicationModalButtonWithHiddenPrices
                    source={props.source}
                    sourceSection={ApplicationSourceSection.MODAL}
                    vendorId={props.vendor.id}
                    offerId={props.offer.id}
                    propertyId={props.propertyId || null}
                    shouldShowPrice={shouldShowPrice}
                    gtmData={gtmData}
                    amplitudePlacement={amplitudePlacement}
                    render={({handleClick}) => (
                        <Button
                            variant="filled_primary"
                            type="button"
                            size={buttonSize}
                            onClick={handleClick}
                            className={buttonStyle}
                            dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_LEAD_FORM_MOBILE}
                        >
                            {buttonText}
                        </Button>
                    )}
                />

                {foundPhone && (
                    <Button
                        iconLeft={PhoneIcon}
                        variant="filled_secondary"
                        size={buttonSize}
                        className={cx(buttonStyle, css({borderColor: "white"}))}
                        onClick={contactPhoneModalOpen}
                        dataTestId={OFFER_TEST_IDS.OFFER_VIEW.CALL_BUTTON_MOBILE}
                    >
                        Zadzwoń
                    </Button>
                )}
                {props.footer}
            </div>

            <CallModal
                isOpen={phoneModalOpen}
                closeModal={() => setPhoneModalOpen(false)}
                vendor={props.vendor}
                offer={props.offer}
                propertyPrice={props.propertyPrice}
                propertyId={props.propertyId || undefined}
                phoneNumber={foundPhone}
                trackUser={trackUser}
            />
        </>
    );
};

const buttonStyle = css({
    minWidth: 0,
    width: "100%"
});

const bottomBarWrapperStyle = css({
    py: 1,
    px: 0.5,
    position: "fixed",
    zIndex: 11,
    bottom: 0,
    left: 0,
    width: "100%",
    boxShadow: "0 2px 20px rgba(0, 0, 0, 0.2)",
    display: "flex",
    flexDirection: "row-reverse",
    gap: 0.5,

    md: {
        display: "none"
    }
});
